import React, { useEffect, useState } from 'react'
import { MenuIcon, CloseIcon, ArrowBackIcon, ArrowForwardIcon } from '../icons'
import { colors } from '../../tokens/colors'
import {
  NavigationContext,
  ContentContainer,
  NavigationContainer,
  HeaderContainer,
  Hamburger,
  NavItemGroupContainer,
  ItemContainer,
  DESKTOP_NAVIGATION_WIDTH,
  MOBILE_NAVIGATION_HEIGHT,
} from './Navigation.styled'
import { NavItem } from './types'
import { Logo } from './components/Logo'
import { NavItemBlock } from './components/NavItemBlock'
import { mediaQueries, useMediaQuery } from '../../tokens/breakpoints'
import LogoIcon from './components/LogoIcon'

export { DESKTOP_NAVIGATION_WIDTH, MOBILE_NAVIGATION_HEIGHT }

export type NavigationProps = {
  orgName: string
  logo?: {
    handleClick?: React.MouseEventHandler
    href?: string
  }
  navItems: NavItem[]
  accountItems: NavItem[]
  activeItemId: string
  isFunder?: boolean
}

const Header = ({
  isMobileOpen,
  setIsMobileOpen,
  handleClick,
  href,
  isFunder = false,
  isCollapsed = false,
}: {
  isMobileOpen: boolean
  setIsMobileOpen: (isMobileOpenValue: boolean) => void
  handleClick?: React.MouseEventHandler
  href?: string
  isFunder?: boolean
  isCollapsed?: boolean
}) => (
  <HeaderContainer
    isMobileOpen={isMobileOpen}
    isFunder={isFunder}
    isCollapsed={isCollapsed}
  >
    <a
      href={href}
      style={{
        display: 'block',
        marginTop: '0',
        textAlign: isCollapsed ? 'center' : 'left',
        width: isCollapsed ? '100%' : 'auto',
      }}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        if (handleClick) {
          e.preventDefault()
          handleClick(e)
        }
      }}
    >
      {isCollapsed ? <LogoIcon /> : <Logo />}
    </a>
    <Hamburger
      onClick={() => {
        setIsMobileOpen(!isMobileOpen)
      }}
    >
      {isMobileOpen ? (
        <CloseIcon color={colors.copy.headerLight} />
      ) : (
        <MenuIcon color={colors.copy.headerLight} />
      )}
    </Hamburger>
  </HeaderContainer>
)

// Update the NavItemGroup component to accept the isCollapsed prop
const NavItemGroup = ({
  items,
  activeItemId,
  isCollapsed,
}: {
  items: NavItem[]
  activeItemId: string
  isCollapsed?: boolean
}) => (
  <NavItemGroupContainer>
    {items.map((item) => (
      <NavItemBlock
        key={item.id}
        item={item}
        activeItemId={activeItemId}
        isCollapsed={isCollapsed}
      />
    ))}
  </NavItemGroupContainer>
)

export const Navigation: React.FC<
  NavigationProps & {
    isCollapsed: boolean
    setIsCollapsed: (isCollapsed: boolean) => void
  }
> = ({
  orgName,
  logo,
  navItems,
  accountItems,
  activeItemId,
  isCollapsed,
  setIsCollapsed,
  isFunder = false,
}) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const isLarge = useMediaQuery(mediaQueries.large.up)

  // Hide the mobile navigation when switched to desktop
  useEffect(() => {
    if (isLarge) {
      setIsMobileOpen(false)
    }
  }, [isLarge])

  // Hide the mobile navigation on page changes
  useEffect(() => {
    setIsMobileOpen(false)
  }, [activeItemId])

  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed)
  }

  const collapseItem = {
    id: 'collapse',
    label: 'Collapse Menu',
    Icon: isCollapsed ? ArrowForwardIcon : ArrowBackIcon,
    handleClick: toggleCollapsed,
  }

  const accountOrgItem = accountItems.find((item) => item.id === 'account')
  if (accountOrgItem) {
    accountOrgItem.label = orgName
  }

  const filteredAccountItems = accountItems.filter(
    (item) => item.id !== 'org' && item.id !== 'account'
  )

  const enhancedAccountItems = [
    ...filteredAccountItems,
    collapseItem,
    ...(accountOrgItem ? [accountOrgItem] : []),
  ]

  return (
    <NavigationContainer
      isMobileOpen={isMobileOpen}
      isFunder={isFunder}
      isCollapsed={isCollapsed}
    >
      <Header
        isMobileOpen={isMobileOpen}
        setIsMobileOpen={setIsMobileOpen}
        handleClick={logo?.handleClick}
        href={logo?.href}
        isFunder={isFunder}
        isCollapsed={isCollapsed}
      />
      <ItemContainer isMobileOpen={isMobileOpen} isCollapsed={isCollapsed}>
        <NavItemGroup
          items={navItems}
          activeItemId={activeItemId}
          isCollapsed={isCollapsed}
        />
        <NavItemGroup
          items={enhancedAccountItems}
          activeItemId={activeItemId}
          isCollapsed={isCollapsed}
        />
      </ItemContainer>
    </NavigationContainer>
  )
}

export const WithNavigation = (
  props: NavigationProps & { children: React.ReactNode }
) => {
  const { children, ...navigationProps } = props
  const [isCollapsed, setIsCollapsed] = useState(false)

  return (
    <NavigationContext isCollapsed={isCollapsed}>
      <Navigation
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        {...navigationProps}
      />
      <ContentContainer>{children}</ContentContainer>
    </NavigationContext>
  )
}
